import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { useTimelineContext } from '../context/TimelineContext';
import Timeline from './Timeline';
import EventForm from './EventForm';
import EventDetail from './EventDetail';
import { useTutorialContext } from '../context/TutorialContext';
import './TimelinePage.css';

const TimelinePage = () => {
  const { timelineId } = useParams();
  const navigate = useNavigate();
  const { userTimelines } = useUserContext();
  const { setCurrentTimelineById, setEditingEvent } = useTimelineContext();
  const { beginnerStepId, goToBeginnerStep, isBeginnerTutorialActive } = useTutorialContext();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const numericTimelineId = Number(timelineId);
  const matchingTimeline = Number.isFinite(numericTimelineId)
    ? userTimelines.find(timeline => timeline.id === numericTimelineId)
    : null;
  
  useEffect(() => {
    setCurrentTimelineById(timelineId);
  }, [timelineId, setCurrentTimelineById]);

  useEffect(() => {
    if (matchingTimeline) return;
    setIsSidebarOpen(false);
    setSelectedEvent(null);
    setEditingEvent(null);
  }, [matchingTimeline, setEditingEvent]);

  useEffect(() => {
    if (!isBeginnerTutorialActive) return;
    if (beginnerStepId !== 'timeline-add' && beginnerStepId !== 'menu-button') return;

    setIsSidebarOpen(false);
    setSelectedEvent(null);
    setEditingEvent(null);
  }, [beginnerStepId, isBeginnerTutorialActive, setEditingEvent]);

  const recoverFromClosedTutorialForm = () => {
    if (!isBeginnerTutorialActive || beginnerStepId !== 'event-form') return;

    goToBeginnerStep(
      'timeline-add',
      'The event form was closed. Click Create Event when you are ready to continue.'
    );
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setEditingEvent(null);
  };

  const closeSidebarAndRecoverTutorial = () => {
    closeSidebar();
    recoverFromClosedTutorialForm();
  };

  const closeEventDetails = () => {
    setSelectedEvent(null);
  };

  const openEventDetails = (event) => {
    if (!event) {
      closeEventDetails();
      setIsSidebarOpen(true);
      return;
    }

    setEditingEvent(null);
    setSelectedEvent(event);
    setIsSidebarOpen(false);
  };

  const openEventEditor = () => {
    if (selectedEvent) {
      setEditingEvent(selectedEvent);
    }
    closeEventDetails();
    setIsSidebarOpen(true);
  };

  const openEventEditorFromMenu = (event) => {
    setSelectedEvent(null);
    setEditingEvent(event);
    setIsSidebarOpen(true);
  };

  const handleEventDeleted = (event) => {
    if (selectedEvent?.id === event.id) {
      setSelectedEvent(null);
    }
    setIsSidebarOpen(false);
  };

  const handleDetailBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      closeEventDetails();
    }
  };

  useEffect(() => {
    if (!selectedEvent) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeEventDetails();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEvent]);

  if (!matchingTimeline) {
    return (
      <div className="main-container">
        <main className="timeline-not-found" aria-labelledby="timeline-not-found-title">
          <div className="timeline-not-found-panel">
            <p className="timeline-not-found-kicker">Missing timeline</p>
            <h2 id="timeline-not-found-title">This timeline could not be found.</h2>
            <p>
              It may have been deleted, belongs to another user ID, or the link may be incorrect.
            </p>
            <button type="button" onClick={() => navigate('/gallery')}>
              Return to Gallery
            </button>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="main-container">
      <div
        className={`sidebar ${isSidebarOpen ? 'open' : ''}`}
        aria-hidden={!isSidebarOpen}
        aria-label="Event editor"
      >
        {isSidebarOpen && (
          <>
            <button className="sidebar-close" onClick={closeSidebarAndRecoverTutorial} aria-label="Close event editor">
              ×
            </button>
            <EventForm onSave={closeSidebar} onCancel={closeSidebarAndRecoverTutorial} onDelete={closeSidebarAndRecoverTutorial} />
          </>
        )}
      </div>
      
      <div className="timeline-container">
        <Timeline
          onEventClick={openEventDetails}
          onEventEdit={openEventEditorFromMenu}
          onEventDelete={handleEventDeleted}
        />
      </div>

      {selectedEvent && (
        <div
          className="event-detail-modal-backdrop"
          onMouseDown={handleDetailBackdropClick}
          role="presentation"
        >
          <EventDetail event={selectedEvent} onClose={closeEventDetails} onEdit={openEventEditor} />
        </div>
      )}
    </div>
  );
};

export default TimelinePage;
